/** Format Date for datetime-local inputs */
export function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Current local datetime for datetime-local `min` attributes (blocks past dates in the picker). */
export function nowDatetimeLocalValue(date: Date = new Date()): string {
  return toDatetimeLocalValue(date.toISOString());
}

export function fromDatetimeLocal(v: string): string | null {
  if (!v || !v.trim()) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function formatApiError(err: unknown): string {
  const e = err as {
    response?: {
      data?: {
        message?: string;
        error?: { message?: string; details?: Record<string, unknown> };
        errors?: Record<string, unknown>;
      };
    };
    message?: string;
  };
  const errors = e?.response?.data?.errors ?? e?.response?.data?.error?.details;
  if (errors && typeof errors === 'object') {
    const parts = Object.entries(errors).flatMap(([, v]) => {
      const extracted = extractApiValidationMessage(v);
      return extracted ? [extracted] : [];
    });
    if (parts.length) return parts.join(' · ');
  }
  const msg = e?.response?.data?.message ?? e?.response?.data?.error?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (e?.message?.trim()) return e.message.trim();
  return 'Something went wrong. Please try again.';
}

/** Map Laravel-style validation errors onto wizard field keys for inline hints. */
export function apiValidationFieldErrors(err: unknown): Record<string, string> {
  const e = err as {
    response?: {
      data?: {
        errors?: Record<string, unknown>;
        error?: { details?: Record<string, unknown> };
      };
    };
  };
  const bag = e?.response?.data?.errors ?? e?.response?.data?.error?.details;
  if (!bag || typeof bag !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [key, msgs] of Object.entries(bag)) {
    const extracted = extractApiValidationMessage(msgs);
    if (extracted) out[key] = extracted;
  }
  return out;
}

function extractApiValidationMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const extracted = extractApiValidationMessage(item);
      if (extracted) return extracted;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    const record = value as { message?: unknown };
    if (typeof record.message === 'string' && record.message.trim()) {
      return record.message.trim();
    }
  }
  return null;
}

export const WIZARD_STEPS = [
  { id: 'basics', title: 'Basics' },
  { id: 'details', title: 'Dates & location' },
  { id: 'participation', title: 'Registration settings' },
  { id: 'registration', title: 'Registration fields' },
  { id: 'tickets', title: 'Tickets' },
  { id: 'advanced', title: 'Advanced' },
  { id: 'review', title: 'Review & publish' },
] as const;

export type EventDateFieldErrors = {
  title?: string;
  custom_event_type?: string;
  start_date?: string;
  end_date?: string;
  registration_start?: string;
  registration_end?: string;
  timezone?: string;
  online_link?: string;
  venue_name?: string;
  venue_city?: string;
  venue_postal_code?: string;
  category?: string;
  cover_image?: string;
  slug?: string;
};

export type EventVisibility = 'public' | 'private' | 'unlisted';

export const DEFAULT_EVENT_TIMEZONE = 'Asia/Kolkata';

function buildTimezoneOptions(): Array<{ value: string; label: string }> {
  const preferred = [
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Hong_Kong',
    'Asia/Bangkok',
    'Asia/Jakarta',
    'Australia/Sydney',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Moscow',
    'Africa/Cairo',
    'Africa/Johannesburg',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'America/Sao_Paulo',
    'Pacific/Auckland',
    'UTC',
  ];

  let zones: string[] = [];
  try {
    const supportedValuesOf = (Intl as typeof Intl & {
      supportedValuesOf?: (key: 'timeZone') => string[];
    }).supportedValuesOf;
    if (typeof supportedValuesOf === 'function') {
      zones = supportedValuesOf('timeZone');
    }
  } catch {
    zones = [];
  }
  if (zones.length === 0) {
    zones = preferred;
  }

  const preferredSet = new Set(preferred);
  const rest = zones.filter((z) => !preferredSet.has(z)).sort((a, b) => a.localeCompare(b));
  const ordered = [...preferred.filter((z) => zones.includes(z) || preferredSet.has(z)), ...rest];

  return ordered.map((value) => ({
    value,
    label: value === 'UTC' ? 'UTC' : value.replace(/_/g, ' '),
  }));
}

/** Full IANA timezone list (preferred hubs first). Built once at module load. */
export const EVENT_TIMEZONE_OPTIONS = buildTimezoneOptions();

export function visibilityFromEvent(event: Record<string, unknown> | null | undefined): EventVisibility {
  if (!event) return 'public';
  const stored = event.visibility;
  if (stored === 'public' || stored === 'private' || stored === 'unlisted') {
    return stored;
  }
  if (event.is_public === false) return 'private';
  const settings = event.settings as Record<string, unknown> | undefined;
  if (settings?.listed === false) return 'unlisted';
  return 'public';
}

export function timezoneLabel(timezone: string): string {
  const match = EVENT_TIMEZONE_OPTIONS.find((opt) => opt.value === timezone);
  return match?.label ?? timezone.replace(/_/g, ' ');
}

export function validateBasicsStep(input: {
  title?: string;
  timezone?: string;
  slug?: string;
}): EventDateFieldErrors {
  const errors: EventDateFieldErrors = {};
  if (!input.title?.trim()) {
    // title handled separately in wizard navigation
  }
  if (!input.timezone?.trim()) {
    errors.timezone = 'Event timezone is required.';
  }
  if (input.slug?.trim()) {
    const normalized = input.slug.trim().toLowerCase();
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) {
      errors.slug = 'Slug may only contain lowercase letters, numbers, and hyphens.';
    }
  }
  return errors;
}

export function validateLocationForMode(input: {
  mode: string;
  online_link?: string;
  venue_name?: string;
  venue_city?: string;
  venue_postal_code?: string;
}): EventDateFieldErrors {
  const errors: EventDateFieldErrors = {};
  const mode = input.mode.toLowerCase();
  const needsVenue = mode === 'offline' || mode === 'hybrid';

  if (needsVenue) {
    if (!input.venue_name?.trim()) {
      errors.venue_name = 'Venue name is required for offline and hybrid events.';
    }
    if (!input.venue_city?.trim()) {
      errors.venue_city = 'Venue city is required for offline and hybrid events.';
    }
  }

  const postal = input.venue_postal_code?.trim();
  if (postal) {
    if (postal.length > 20) {
      errors.venue_postal_code = 'Postal code may be at most 20 characters.';
    } else if (!/^[A-Za-z0-9][A-Za-z0-9\s\-]{0,18}[A-Za-z0-9]$|^[A-Za-z0-9]$/.test(postal)) {
      errors.venue_postal_code = 'Postal code may only contain letters, numbers, spaces, and hyphens.';
    }
  }

  return errors;
}

export function validateCategorySelection(input: {
  category?: string;
  allowedSlugs: string[];
  categoriesLoaded: boolean;
}): EventDateFieldErrors {
  const errors: EventDateFieldErrors = {};
  return errors;
}

export function emptyVenueFields() {
  return {
    venue_name: '',
    venue_address: '',
    venue_city: '',
    venue_state: '',
    venue_country: '',
    venue_postal_code: '',
  };
}

export function showVenueFields(mode: string): boolean {
  const normalized = String(mode ?? '').trim().toLowerCase();
  return normalized === 'offline' || normalized === 'hybrid';
}

export function showOnlineLinkField(mode: string): boolean {
  const normalized = String(mode ?? '').trim().toLowerCase();
  return normalized === 'online' || normalized === 'hybrid';
}

export function validateDetailsStep(input: {
  mode: string;
  start_date?: string;
  end_date?: string;
  registration_start?: string;
  registration_end?: string;
  online_link?: string;
  venue_name?: string;
  venue_city?: string;
  venue_postal_code?: string;
}): EventDateFieldErrors {
  return {
    ...validateEventDates({
      start_date: input.start_date,
      end_date: input.end_date,
      registration_start: input.registration_start,
      registration_end: input.registration_end,
    }),
    ...validateLocationForMode({
      mode: input.mode,
      online_link: input.online_link,
      venue_name: input.venue_name,
      venue_city: input.venue_city,
      venue_postal_code: input.venue_postal_code,
    }),
  };
}

export function firstValidationMessage(errors: Record<string, string | undefined>): string | null {
  const values = Object.values(errors).filter(Boolean);
  return values[0] ?? null;
}

/** Focus / scroll to the first field that has a validation error. */
export function focusFirstInvalidField(errors: Record<string, string | undefined>): void {
  if (typeof document === 'undefined') return;
  const firstKey = Object.keys(errors).find((k) => Boolean(errors[k]));
  if (!firstKey) return;
  const el =
    document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`) ??
    document.querySelector<HTMLElement>(`[data-error-key="${firstKey}"]`) ??
    document.querySelector<HTMLElement>(`[name="${firstKey}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (typeof el.focus === 'function') {
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }
}

export type TicketSaleFieldErrors = {
  sale_start?: string;
  sale_end?: string;
};

function parseLocalDate(value: string | null | undefined): Date | null {
  if (!value || !value.trim()) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Validate event + registration date relationships. */
export function validateEventDates(input: {
  start_date?: string;
  end_date?: string;
  registration_start?: string;
  registration_end?: string;
  /** Defaults to Date.now(); inject in tests. */
  now?: Date;
}): EventDateFieldErrors {
  const errors: EventDateFieldErrors = {};
  const start = parseLocalDate(input.start_date);
  const end = parseLocalDate(input.end_date);
  const regOpen = parseLocalDate(input.registration_start);
  const regClose = parseLocalDate(input.registration_end);
  const now = input.now ?? new Date();

  if (start && start.getTime() <= now.getTime()) {
    errors.start_date = 'Event start cannot be in the past.';
  }
  if (end && end.getTime() <= now.getTime()) {
    errors.end_date = 'Event end cannot be in the past.';
  }
  if (start && end && end <= start) {
    errors.end_date = 'Event end must be after event start.';
  }
  if (regOpen && regClose && regClose < regOpen) {
    errors.registration_end = 'Registration close must be on or after registration open.';
  }
  if (regClose && start && regClose > start) {
    errors.registration_end = 'Registration must close before event start date.';
  }
  if (regOpen && start && regOpen > start) {
    errors.registration_start = 'Registration open must be on or before event start.';
  }

  return errors;
}

/** Validate ticket sale window against registration period (when dates are provided). */
export function validateTicketSaleDates(input: {
  sale_start?: string;
  sale_end?: string;
  registration_start?: string;
  registration_end?: string;
}): TicketSaleFieldErrors {
  const errors: TicketSaleFieldErrors = {};
  const saleStart = parseLocalDate(input.sale_start);
  const saleEnd = parseLocalDate(input.sale_end);
  const regOpen = parseLocalDate(input.registration_start);
  const regClose = parseLocalDate(input.registration_end);

  if (saleStart && saleEnd && saleEnd < saleStart) {
    errors.sale_end = 'Ticket sale end must be on or after sale start.';
  }
  if (regOpen && saleStart && saleStart < regOpen) {
    errors.sale_start = 'Ticket sale start must fall within the registration window.';
  }
  if (regClose && saleEnd && saleEnd > regClose) {
    errors.sale_end = 'Ticket sale end must fall within the registration window.';
  }
  if (regClose && saleStart && saleStart > regClose) {
    errors.sale_start = 'Ticket sale start must fall within the registration window.';
  }

  return errors;
}

export const MANDATORY_RULE_FIELDS = [
  { key: 'rules_text', label: 'Rules text' },
  { key: 'eligibility_criteria', label: 'Eligibility criteria' },
  { key: 'code_of_conduct', label: 'Code of conduct' },
  { key: 'disqualification_rules', label: 'Disqualification rules' },
] as const;

export type RulesFormState = Record<(typeof MANDATORY_RULE_FIELDS)[number]['key'], string>;

export function emptyRulesForm(): RulesFormState {
  return {
    rules_text: '',
    eligibility_criteria: '',
    code_of_conduct: '',
    disqualification_rules: '',
  };
}

export function rulesFromSettings(settings: Record<string, unknown> | null | undefined): RulesFormState {
  const s = settings ?? {};
  return {
    rules_text: typeof s.rules_text === 'string' ? s.rules_text : '',
    eligibility_criteria: typeof s.eligibility_criteria === 'string' ? s.eligibility_criteria : '',
    code_of_conduct: typeof s.code_of_conduct === 'string' ? s.code_of_conduct : '',
    disqualification_rules: typeof s.disqualification_rules === 'string' ? s.disqualification_rules : '',
  };
}

import { richTextHasContent } from '@/components/event-wizard/wizard-rich-text-utils';

/** Returns field keys that are still empty. */
export function getMissingRulesFields(rules: RulesFormState): string[] {
  return MANDATORY_RULE_FIELDS.filter(({ key }) => !richTextHasContent(rules[key] ?? '')).map(({ key }) => key);
}

export function isRulesComplete(rules: RulesFormState): boolean {
  return getMissingRulesFields(rules).length === 0;
}

export function wizardStepStorageKey(eventId: string): string {
  return `event_wizard_step_${eventId}`;
}

/** Terminal / locked lifecycle states — wizard editing is not allowed. */
const BLOCKED_WIZARD_STATUSES = new Set(['archived', 'cancelled', 'completed', 'suspended']);

/** Pre-publish states where organizers are still completing the creation wizard. */
const PRE_PUBLISH_WIZARD_STATUSES = new Set([
  'draft',
  'pending_review',
  'ready_for_review',
  'pending_approval',
  'approved',
]);

function normalizeEventStatus(status: string | undefined): string {
  const normalized = String(status ?? 'draft').trim().toLowerCase();
  return normalized || 'draft';
}

export function isWizardBlockedStatus(status: string | undefined): boolean {
  return BLOCKED_WIZARD_STATUSES.has(normalizeEventStatus(status));
}

export function isWizardEditableStatus(status: string | undefined): boolean {
  return !isWizardBlockedStatus(status);
}

/** Show the overview "Complete setup" CTA while the event can still be finished in the wizard. */
export function shouldShowCompleteSetupBanner(
  status: string | undefined,
  workflowHealth?: number | null,
): boolean {
  if (!isWizardEditableStatus(status)) return false;

  const normalized = normalizeEventStatus(status);
  if (PRE_PUBLISH_WIZARD_STATUSES.has(normalized)) return true;

  if (normalized === 'published' || normalized === 'live') {
    return workflowHealth != null && workflowHealth < 100;
  }

  return false;
}

/** Read persisted step; returns null if missing or invalid. */
export function readPersistedWizardStep(eventId: string): number | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(wizardStepStorageKey(eventId));
  if (raw === null) return null;
  const idx = Number.parseInt(raw, 10);
  if (!Number.isInteger(idx) || idx < 0 || idx >= WIZARD_STEPS.length) return null;
  return idx;
}

export function persistWizardStep(eventId: string, stepIndex: number): void {
  if (typeof window === 'undefined') return;
  if (stepIndex < 0 || stepIndex >= WIZARD_STEPS.length) return;
  sessionStorage.setItem(wizardStepStorageKey(eventId), String(stepIndex));
}

export function clearPersistedWizardStep(eventId: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(wizardStepStorageKey(eventId));
}
